import type { WithId } from '@quromedical/fhir-common';
import { compact, first } from '@quromedical/utils';
import { isValid, parseISO } from 'date-fns';
import type {
  QuestionnaireItem,
  QuestionnaireResponseItem,
  QuestionnaireResponseItemAnswer,
} from 'fhir/r4';

import {
  type AnswerUpdate,
  type QuestionnaireResponse,
  QuestionnaireResponseSchema,
  type QuestionResponseAnswer,
  type UpdateAnswer,
} from './schema';

const mapFhirItemToModelSections = (
  questionnaireResponseItem: QuestionnaireResponseItem[] | undefined,
): QuestionResponseAnswer[] => {
  const result = compact(
    questionnaireResponseItem?.map((section) => {
      const { text, item, linkId, answer } = section;
      const mappedAnswer = answer?.map((a) => {
        return {
          item:
            a.item && a.item.length
              ? mapFhirItemToModelSections(a.item)
              : undefined,
          valueString: a.valueString,
          valueInteger: a.valueInteger,
          valueBoolean: a.valueBoolean,
          valueDate: a.valueDate ? a.valueDate.split('T')[0] : undefined,
        };
      });

      return {
        text,
        linkId,
        required: true,
        answer: mappedAnswer,
        section:
          item && item.length ? mapFhirItemToModelSections(item) : undefined,
      };
    }),
  );

  return result;
};

export const mapFhirQuestionnaireResponseToModel = (
  fhirQuestionnaireResponse: fhir4.QuestionnaireResponse,
): QuestionnaireResponse => {
  const { item, status, id, questionnaire, encounter, subject, basedOn } =
    fhirQuestionnaireResponse;

  const questionnaireResponse = {
    id,
    questionnaire,
    admission: encounter,
    patient: subject,
    careplan: first(basedOn),
    status,
    section: mapFhirItemToModelSections(item),
  };

  const validatedQuestionnaireResponse = QuestionnaireResponseSchema.safeParse(
    questionnaireResponse,
  );

  if (!validatedQuestionnaireResponse.success) {
    throw new Error(
      JSON.stringify(validatedQuestionnaireResponse.error, null, 2),
    );
  }

  return validatedQuestionnaireResponse.data;
};

const mapModelSectionsToFhirItem = (
  section: QuestionResponseAnswer[],
): QuestionnaireResponseItem[] => {
  const result = compact(
    section.map((sec) => {
      const { text, linkId, answer, section } = sec;
      return {
        text,
        linkId,
        answer,
        item: section ? mapModelSectionsToFhirItem(section) : undefined,
      };
    }),
  );

  return result;
};

export const mapQuestionnaireResponseToModelToFhir = (
  questionnaireResponse: QuestionnaireResponse,
): fhir4.QuestionnaireResponse => {
  const { section, patient, admission, careplan, questionnaire, status, id } =
    questionnaireResponse;

  const fhirQuestionnaireResponse: fhir4.QuestionnaireResponse = {
    resourceType: 'QuestionnaireResponse',
    questionnaire: questionnaire,
    id,
    status,
    source: patient,
    subject: patient,
    encounter: admission,
    basedOn: careplan ? [careplan] : undefined,
    item: section ? mapModelSectionsToFhirItem(section) : undefined,
  };

  return fhirQuestionnaireResponse;
};

const isISODateString = (value: string): boolean => {
  const parsed = parseISO(value);
  return isValid(parsed);
};

const getValueField = (value: AnswerUpdate) => {
  const isValidISODate = typeof value === 'string' && isISODateString(value);
  const isBooleanString =
    typeof value === 'string' && (value === 'Yes' || value === 'No');

  if (isValidISODate) {
    return { valueDate: value?.split('T')[0] };
  }

  if (isBooleanString) {
    return { valueBoolean: value === 'Yes' ? true : false };
  }

  const typeToFieldMap: Record<string, QuestionnaireResponseItemAnswer> = {
    string: { valueString: value as string },
    boolean: { valueBoolean: value as boolean },
    number: { valueInteger: value as number },
    object: {
      valueDate:
        value instanceof Date ? value.toISOString().split('T')[0] : undefined,
    },
  };

  const fieldMapper = typeToFieldMap[typeof value];
  return fieldMapper;
};

const buildResponseItems = (
  updates: UpdateAnswer[],
  questions: QuestionnaireItem[],
): QuestionnaireResponseItem[] => {
  const responseItems = questions.map((question) => {
    const matchingUpdate = updates.find(
      (update) => update.linkId === question.linkId,
    );
    const answer = matchingUpdate ? matchingUpdate.answer : undefined;

    const value = answer
      ? Array.isArray(answer)
        ? answer.map(getValueField)
        : [
            {
              ...getValueField(answer),
              item: question.item
                ? buildResponseItems(updates, question.item)
                : undefined,
            },
          ]
      : undefined;

    const shouldAddResponse = value || question.item;

    if (!shouldAddResponse) {
      return;
    }

    return {
      linkId: question.linkId,
      text: question.text,
      answer: value,
      item:
        question.item && value === undefined
          ? buildResponseItems(updates, question.item)
          : undefined,
    };
  });
  return compact(responseItems);
};

export const mapQuestionnaireResponseUpdateModelToFhir = (
  questionnaireResponse: WithId<fhir4.QuestionnaireResponse>,
  answers: UpdateAnswer[],
  questionnaireItems: fhir4.QuestionnaireItem[],
): WithId<fhir4.QuestionnaireResponse> => {
  return {
    ...questionnaireResponse,
    status: 'completed',
    item: buildResponseItems(answers, questionnaireItems),
  };
};
